import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Gift, Eye, Download, Loader2, Heart, Clock, Check, X, FileText, Send, CreditCard, Copy, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PdfImagePreview({ url, onClose, onDownload, downloading, title }) {
  if (!url) return null;
  return (
    <div data-testid="pdf-preview-modal" className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex flex-col" onClick={onClose}>
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10 bg-black/70 shrink-0">
        <p className="font-display text-sm text-white/80 truncate">{title || "Náhľad"}</p>
        <div className="flex items-center gap-2 shrink-0">
          {onDownload && (
            <button type="button" onClick={(e) => { e.stopPropagation(); onDownload(); }} disabled={downloading} data-testid="preview-download-btn"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-3 py-2 text-sm hover:bg-[#F2E3C6] transition-colors disabled:opacity-50">
              {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Stiahnuť
            </button>
          )}
          <button type="button" onClick={onClose} data-testid="preview-close-btn"
            className="inline-flex items-center gap-2 border border-white/20 text-white/80 px-3 py-2 text-sm hover:border-red-400 hover:text-red-400 transition-colors">
            <X size={14} /> Zavrieť
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <img src={url} alt="Náhľad dokumentu" data-testid="preview-image" className="mx-auto max-w-full sm:max-w-3xl h-auto shadow-2xl bg-white" />
      </div>
    </div>
  );
}

export default function CharitativnaCinnost({ token, role = "admin" }) {
  return (
    <div data-testid="charity-page">
      <div className="mb-6">
        <h2 className="font-display text-2xl mb-1 flex items-center gap-2">
          <Heart size={22} className="text-[#D4AF37]" /> Charitatívna činnosť
        </h2>
        <p className="text-white/40 text-sm">Spoločne robíme svet lepším. Nástroje pre dobročinnú a reprezentačnú činnosť SauberEko®.</p>
      </div>
      <GiftCheque token={token} role={role} />
      <div className="mt-10 border-t border-white/10 pt-8">
        <CharityContract token={token} role={role} />
      </div>
      <div className="mt-10 border-t border-white/10 pt-8">
        <PublicCardLink />
      </div>
      <div className="mt-10 border-t border-white/10 pt-8">
        <BusinessCard token={token} />
      </div>
    </div>
  );
}

function PublicCardLink() {
  const [copied, setCopied] = useState("");
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const backend = process.env.REACT_APP_BACKEND_URL;
  const people = [
    { slug: "timea", label: "Timea Smrktová — Riaditeľka pre marketing a styk s verejnosťou" },
    { slug: "martin", label: "Martin Smrkta — Zakladateľ SauberEko® a konateľ SauberEko s. r. o." },
  ];
  const copy = async (slug) => {
    try { await navigator.clipboard.writeText(`${backend}/api/vizitka/${slug}`); setCopied(slug); setTimeout(() => setCopied(""), 1800); } catch { /* ignore */ }
  };
  return (
    <div data-testid="public-card-section">
      <div className="mb-4">
        <h3 className="font-display text-xl mb-1 flex items-center gap-2"><CreditCard size={20} className="text-[#D4AF37]" /> Verejné vizitky (zdieľateľné)</h3>
        <p className="text-white/40 text-sm">Online vizitky — mobilné, s logom, QR kódom a tlačidlom „Uložiť kontakt" (vCard). Pri zdieľaní sa zobrazí pekný náhľadový obrázok, nielen odkaz. Verejne dostupné komukoľvek s odkazom.</p>
      </div>
      <div className="space-y-4">
        {people.map((pp) => (
          <div key={pp.slug} className="border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/[0.06] to-transparent p-5" data-testid={`public-card-${pp.slug}`}>
            <p className="text-white/80 text-sm mb-3">{pp.label}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-[#D4AF37] text-xs sm:text-sm bg-[#111] border border-white/10 px-3 py-2.5 flex-1 min-w-0 truncate" data-testid={`public-card-url-${pp.slug}`}>{origin}/vizitka/{pp.slug}</code>
              <button type="button" onClick={() => copy(pp.slug)} data-testid={`public-card-copy-${pp.slug}`}
                className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm text-white/80 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                {copied === pp.slug ? <Check size={15} /> : <Copy size={15} />} {copied === pp.slug ? "Skopírované" : "Kopírovať"}
              </button>
              <a href={`${origin}/vizitka/${pp.slug}`} target="_blank" rel="noreferrer" data-testid={`public-card-open-${pp.slug}`}
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-4 py-2.5 text-sm hover:bg-[#F2E3C6] transition-colors">
                <ExternalLink size={15} /> Otvoriť
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BusinessCard({ token }) {
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const get = async (which, mode) => {
    setBusy(`${which}-${mode}`); setErr("");
    try {
      const res = await axios.get(`${API}/admin/business-card`, { ...auth, params: { which }, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      if (mode === "download") {
        const a = document.createElement("a");
        a.href = url; a.download = `vizitka-timea-${which}-tlac.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      } else { window.open(url, "_blank"); }
    } catch { setErr("Nepodarilo sa načítať vizitku."); }
    finally { setBusy(""); }
  };

  const variants = [
    { key: "full", label: "Obojstranná (2 strany)" },
    { key: "front", label: "Predná strana" },
    { key: "back", label: "Zadná strana" },
  ];

  return (
    <div data-testid="business-card-section">
      <div className="mb-4">
        <h3 className="font-display text-xl mb-1 flex items-center gap-2"><CreditCard size={20} className="text-[#D4AF37]" /> Tlačová vizitka</h3>
        <p className="text-white/40 text-sm">Luxusná obojstranná vizitka (Timea Smrktová) pripravená pre tlačiareň: formát 85×55 mm, 3 mm spadávka, orezové značky, CMYK, 300 DPI. Stiahnite a odovzdajte priamo tlačiarni.</p>
        <p className="text-[#D4AF37]/80 text-xs mt-1">Len pre prihlásených — nie je verejne dostupná.</p>
      </div>
      <div className="border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/[0.06] to-transparent p-6">
        {err && <p className="text-red-400 text-sm mb-3" data-testid="business-card-error">{err}</p>}
        <div className="space-y-3">
          {variants.map((v) => (
            <div key={v.key} className="flex items-center justify-between gap-3 flex-wrap">
              <span className="text-white/80 text-sm">{v.label}</span>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={() => get(v.key, "preview")} disabled={!!busy} data-testid={`business-card-preview-${v.key}`}
                  className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm text-white/80 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-50">
                  {busy === `${v.key}-preview` ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Náhľad
                </button>
                <button type="button" onClick={() => get(v.key, "download")} disabled={!!busy} data-testid={`business-card-download-${v.key}`}
                  className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-4 py-2.5 text-sm hover:bg-[#F2E3C6] transition-colors disabled:opacity-50">
                  {busy === `${v.key}-download` ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Stiahnuť
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CharityContract({ token, role }) {
  const empty = {
    provider_name: "", provider_address: "", provider_ico: "", provider_dic: "",
    provider_form: "", provider_rep: "", provider_bank: "", provider_iban: "",
    provider_city: "", provider_email: "", amount: "", city: "Nováky", payment_days: "14", valid_until: "31. 12. 2026",
  };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [contracts, setContracts] = useState([]);
  const [preview, setPreview] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadContracts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/charity-contracts`, auth);
      setContracts(data || []);
    } catch { /* ignore */ }
  }, [token]);
  useEffect(() => { loadContracts(); }, [loadContracts]);

  const make = async (action) => {
    setBusy(action); setErr(""); setMsg("");
    try {
      if (action === "email") {
        if (!f.provider_email.trim() || !f.provider_email.includes("@")) { setErr("Zadajte platný e-mail poskytovateľa."); setBusy(""); return; }
        const { data } = await axios.post(`${API}/admin/charity-contract`, { ...f, action }, auth);
        setMsg(`Zmluva bola odoslaná na ${data.to}.`);
        loadContracts();
      } else {
        const res = await axios.post(`${API}/admin/charity-contract`, { ...f, action }, { ...auth, responseType: "blob" });
        const url = window.URL.createObjectURL(res.data);
        if (action === "download") {
          const a = document.createElement("a");
          a.href = url; a.download = "zmluva-charitativna-reklama-saubereko.pdf";
          document.body.appendChild(a); a.click(); a.remove();
          loadContracts();
        } else { setPreview(url); }
      }
    } catch { setErr("Operácia zlyhala. Skúste znova."); }
    finally { setBusy(""); }
  };

  const inp = (k, label, ph) => (
    <label className="block" key={k}>
      <span className="block text-xs text-white/50 mb-1.5">{label}</span>
      <input value={f[k]} onChange={set(k)} placeholder={ph} data-testid={`contract-${k}`}
        className="w-full bg-[#111] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37] transition-colors" />
    </label>
  );

  return (
    <div data-testid="charity-contract-section">
      <div className="mb-4">
        <h3 className="font-display text-xl mb-1 flex items-center gap-2"><FileText size={20} className="text-[#D4AF37]" /> Zmluva o charitatívnej reklame</h3>
        <p className="text-white/40 text-sm">Objednávateľ (SauberEko s.r.o. + Martin Smrkta, konateľ) je predvyplnený vrátane podpisu a pečate. Doplňte údaje poskytovateľa (charita/OZ/nadácia), sumu a dátum — prázdne polia ostanú ako linka na vypísanie.</p>
      </div>
      <div className="border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/[0.06] to-transparent p-6">
        <p className="text-xs text-[#D4AF37]/80 uppercase tracking-wider mb-3">Poskytovateľ (charita / OZ / nadácia)</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {inp("provider_name", "Názov organizácie", "napr. Nadácia Dobré srdce")}
          {inp("provider_address", "Sídlo", "ulica, mesto")}
          {inp("provider_ico", "IČO", "IČO")}
          {inp("provider_dic", "DIČ (ak má)", "DIČ")}
          {inp("provider_form", "Právna forma", "napr. Občianske združenie")}
          {inp("provider_rep", "Štatutár (meno)", "meno a priezvisko")}
          {inp("provider_bank", "Banka", "názov banky")}
          {inp("provider_iban", "IBAN", "SK...")}
          {inp("provider_email", "E-mail poskytovateľa (na odoslanie)", "email@organizacia.sk")}
        </div>
        <p className="text-xs text-[#D4AF37]/80 uppercase tracking-wider mt-5 mb-3">Odmena a termíny</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {inp("amount", "Odmena (slovom aj číslom)", "napr. jedentisíc eur (1 000 €)")}
          {inp("city", "Miesto podpisu", "Nováky")}
          {inp("payment_days", "Splatnosť (dní)", "14")}
          {inp("valid_until", "Platnosť do", "31. 12. 2026")}
        </div>
        {err && <p className="text-red-400 text-sm mt-3" data-testid="contract-error">{err}</p>}
        {msg && <p className="text-emerald-300 text-sm mt-3" data-testid="contract-msg">{msg}</p>}
        <div className="flex items-center gap-2 flex-wrap mt-5">
          <button type="button" onClick={() => make("preview")} disabled={!!busy} data-testid="contract-preview-btn"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm text-white/80 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-50">
            {busy === "preview" ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Náhľad
          </button>
          <button type="button" onClick={() => make("download")} disabled={!!busy} data-testid="contract-download-btn"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-4 py-2.5 text-sm hover:bg-[#F2E3C6] transition-colors disabled:opacity-50">
            {busy === "download" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Stiahnuť zmluvu
          </button>
          <button type="button" onClick={() => make("email")} disabled={!!busy} data-testid="contract-email-btn"
            className="inline-flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2.5 text-sm hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50">
            {busy === "email" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Poslať e-mailom
          </button>
        </div>
      </div>

      {contracts.length > 0 && (
        <div className="mt-8" data-testid="contract-history">
          <h3 className="font-display text-lg mb-3">{role === "marketing" ? "Moje vystavené zmluvy" : "Evidencia vystavených zmlúv"}</h3>
          <div className="space-y-2">
            {contracts.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 border border-white/10 bg-[#0a0a0a] px-4 py-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-white/90 text-sm">{c.provider_name || "(bez názvu)"}{c.amount ? ` · ${c.amount}` : ""}</p>
                  <p className="text-white/40 text-xs mt-0.5">
                    {new Date(c.created_at).toLocaleDateString("sk-SK")} · {c.issued_by_name}
                    {c.sent ? <span className="text-emerald-300"> · odoslané {c.provider_email}</span> : <span className="text-white/40"> · stiahnuté</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <PdfImagePreview url={preview} onClose={() => setPreview("")} title="Náhľad zmluvy o charitatívnej reklame"
        onDownload={() => make("download")} downloading={busy === "download"} />
    </div>
  );
}

function GiftCheque({ token, role }) {
  const [amount, setAmount] = useState("150");
  const [recipient, setRecipient] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [requests, setRequests] = useState([]);
  const [actionId, setActionId] = useState("");
  const [preview, setPreview] = useState("");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/cheque-requests`, auth);
      setRequests(data || []);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const downloadBlob = (blob, name = "darcovsky-sek-saubereko.pdf") => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const makePdf = async (mode) => {
    if (!String(amount).trim()) { setErr("Zadajte sumu."); return; }
    setBusy(mode); setErr(""); setMsg("");
    try {
      const res = await axios.post(`${API}/admin/gift-cheque`,
        { amount: String(amount).trim(), recipient: recipient.trim(), mode },
        { ...auth, responseType: "blob" });
      // Marketing nad limit -> server vráti JSON (čaká na schválenie)
      if (res.data && res.data.type && res.data.type.includes("json")) {
        const text = await res.data.text();
        const j = JSON.parse(text);
        setMsg(j.message || "Žiadosť odoslaná na schválenie.");
        loadRequests();
      } else if (mode === "issue") {
        downloadBlob(res.data);
        loadRequests();
      } else {
        setPreview(window.URL.createObjectURL(res.data));
      }
    } catch { setErr("Nepodarilo sa vytvoriť šek."); }
    finally { setBusy(""); }
  };

  const approve = async (id) => {
    setActionId(id);
    try {
      await axios.post(`${API}/admin/cheque-requests/${id}/approve`, {}, auth);
      const res = await axios.get(`${API}/admin/cheque-requests/${id}/pdf`, { ...auth, responseType: "blob" });
      downloadBlob(res.data);
      loadRequests();
    } catch { setErr("Schválenie zlyhalo."); }
    finally { setActionId(""); }
  };

  const reject = async (id) => {
    setActionId(id);
    try {
      await axios.post(`${API}/admin/cheque-requests/${id}/reject`, {}, auth);
      loadRequests();
    } catch { setErr("Zamietnutie zlyhalo."); }
    finally { setActionId(""); }
  };

  const download = async (id) => {
    setActionId(id);
    try {
      const res = await axios.get(`${API}/admin/cheque-requests/${id}/pdf`, { ...auth, responseType: "blob" });
      downloadBlob(res.data);
    } catch { setErr("Stiahnutie zlyhalo."); }
    finally { setActionId(""); }
  };

  const pending = requests.filter((r) => r.status === "pending");
  const decided = requests.filter((r) => r.status !== "pending");

  const StatusBadge = ({ s }) => {
    const map = {
      pending: ["Čaká na schválenie", "bg-amber-500/15 text-amber-300"],
      approved: ["Schválené", "bg-emerald-500/15 text-emerald-300"],
      rejected: ["Zamietnuté", "bg-red-500/15 text-red-300"],
    };
    const [label, cls] = map[s] || [s, "bg-white/10 text-white/60"];
    return <span className={`text-[10px] px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
  };

  return (
    <div data-testid="gift-cheque-section">
      <div className="mb-4">
        <h3 className="font-display text-xl mb-1 flex items-center gap-2"><Gift size={20} className="text-[#D4AF37]" /> Darčovský šek</h3>
        <p className="text-white/40 text-sm">Nádherný zlatý šek na tlač (A4 našírku). Zadajte sumu a príjemcu — podpis a pečať sú predvyplnené. Číslo šeku (Sa.Šek č. 2026-…) sa priradí automaticky pri vystavení.</p>
        <p className="text-[#D4AF37]/80 text-xs mt-1">Šeky nad 300 € schvaľuje zakladateľ.</p>
      </div>
      <div className="border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/[0.06] to-transparent p-6">
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
          <label className="block">
            <span className="block text-xs text-white/50 mb-1.5">Suma (€)</span>
            <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ""))}
              inputMode="numeric" placeholder="napr. 150" data-testid="cheque-amount"
              className="w-full bg-[#111] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37] transition-colors" />
          </label>
          <label className="block">
            <span className="block text-xs text-white/50 mb-1.5">Príjemca (nepovinné)</span>
            <input value={recipient} onChange={(e) => setRecipient(e.target.value)}
              placeholder="napr. Jana Nováková" data-testid="cheque-recipient"
              className="w-full bg-[#111] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37] transition-colors" />
          </label>
        </div>
        {err && <p className="text-red-400 text-sm mt-3" data-testid="cheque-error">{err}</p>}
        {msg && <p className="text-amber-300 text-sm mt-3" data-testid="cheque-msg">{msg}</p>}
        <div className="flex items-center gap-2 flex-wrap mt-4">
          <button type="button" onClick={() => makePdf("preview")} disabled={!!busy} data-testid="cheque-preview-btn"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm text-white/80 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-50">
            {busy === "preview" ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Náhľad
          </button>
          <button type="button" onClick={() => makePdf("issue")} disabled={!!busy} data-testid="cheque-issue-btn"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-4 py-2.5 text-sm hover:bg-[#F2E3C6] transition-colors disabled:opacity-50">
            {busy === "issue" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Vystaviť a stiahnuť
          </button>
        </div>
      </div>

      {/* Žiadosti o schválenie (admin) */}
      {role === "admin" && pending.length > 0 && (
        <div className="mt-8" data-testid="cheque-approvals">
          <h3 className="font-display text-lg mb-3 flex items-center gap-2"><Clock size={18} className="text-amber-300" /> Žiadosti o schválenie ({pending.length})</h3>
          <div className="space-y-2">
            {pending.map((r) => (
              <div key={r.id} data-testid="cheque-request-row" className="flex items-center justify-between gap-3 border border-white/10 bg-[#0a0a0a] px-4 py-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-white/90 text-sm"><span className="text-[#D4AF37] font-semibold">{r.amount}€</span>{r.recipient ? ` · pre ${r.recipient}` : ""}</p>
                  <p className="text-white/40 text-xs mt-0.5">Žiada: {r.requested_by_name}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => approve(r.id)} disabled={actionId === r.id} data-testid="cheque-approve-btn"
                    className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-3 py-2 text-xs hover:bg-emerald-500 transition-colors disabled:opacity-50">
                    {actionId === r.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Schváliť a stiahnuť
                  </button>
                  <button onClick={() => reject(r.id)} disabled={actionId === r.id} data-testid="cheque-reject-btn"
                    className="inline-flex items-center gap-1.5 border border-white/15 text-white/60 px-3 py-2 text-xs hover:border-red-400 hover:text-red-400 transition-colors disabled:opacity-50">
                    <X size={14} /> Zamietnuť
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Moje žiadosti (marketing) + história (admin) */}
      {(role === "marketing" ? requests : decided).length > 0 && (
        <div className="mt-8" data-testid="cheque-request-history">
          <h3 className="font-display text-lg mb-3">{role === "marketing" ? "Moje žiadosti" : "História žiadostí"}</h3>
          <div className="space-y-2">
            {(role === "marketing" ? requests : decided).map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 border border-white/10 bg-[#0a0a0a] px-4 py-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-white/90 text-sm flex items-center gap-2">
                    <span className="text-[#D4AF37] font-semibold">{r.amount}€</span>{r.recipient ? ` · pre ${r.recipient}` : ""} <StatusBadge s={r.status} />
                  </p>
                  <p className="text-white/40 text-xs mt-0.5">{r.number ? `${r.number} · ` : ""}{role === "admin" ? `Žiada: ${r.requested_by_name}` : ""}</p>
                </div>
                {r.status === "approved" && (
                  <button onClick={() => download(r.id)} disabled={actionId === r.id} data-testid="cheque-download-btn"
                    className="inline-flex items-center gap-1.5 border border-[#D4AF37]/50 text-[#D4AF37] px-3 py-2 text-xs hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50 shrink-0">
                    {actionId === r.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Stiahnuť
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <PdfImagePreview url={preview} onClose={() => setPreview("")} title="Náhľad darčovského šeku" />
    </div>
  );
}
